import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStarterContext } from '@/context/StarterContext';
import {
  getDaysSinceFeeding,
  getHealthStatus,
  formatRelativeTime,
  formatFeedingDate,
  getStarterAgeDays,
  getAverageFeedingInterval,
} from '@/lib/dateUtils';
import { Card } from '@/components/ui/Card';
import { HealthIndicator } from '@/components/ui/HealthIndicator';
import { ErrorState } from '@/components/ui/ErrorState';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import styles from '@/styles/components/DashboardPage.module.css';

const SOURCE_LABELS: Record<string, string> = {
  app: 'app',
  email: 'email',
  squad: 'squad',
};

export function DashboardPage() {
  const navigate = useNavigate();
  const { starter, feedings, isLoading, error, recordFeeding, deleteFeeding, refresh } =
    useStarterContext();
  const [isFeedingInProgress, setIsFeedingInProgress] = useState(false);
  const [toast, setToast] = useState<{ message: string; feedingId?: string } | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const toastTimer = useRef<ReturnType<typeof setTimeout>>(null);

  // Auto-dismiss toast after 5 seconds
  useEffect(() => {
    if (toast) {
      toastTimer.current = setTimeout(() => setToast(null), 5000);
      return () => {
        if (toastTimer.current) clearTimeout(toastTimer.current);
      };
    }
  }, [toast]);

  const handleFeedStarter = useCallback(async () => {
    if (isFeedingInProgress) return;
    setIsFeedingInProgress(true);
    try {
      await recordFeeding(new Date().toISOString());
      setToast({
        message: '✓ Starter fed! Next feeding due in 3-5 days.',
      });
    } catch {
      setToast({ message: '⚠ Failed to record feeding. Try again.' });
    } finally {
      setIsFeedingInProgress(false);
    }
  }, [isFeedingInProgress, recordFeeding]);

  const handleUndo = useCallback(async () => {
    if (feedings.length > 0) {
      try {
        await deleteFeeding(feedings[0].id);
        setToast(null);
      } catch {
        // Undo failed — silent
      }
    }
  }, [feedings, deleteFeeding]);

  const handleDeleteRecent = useCallback(async () => {
    if (feedings.length > 0) {
      try {
        await deleteFeeding(feedings[0].id);
        setShowDeleteConfirm(false);
      } catch {
        // Delete failed
      }
    }
  }, [feedings, deleteFeeding]);

  if (isLoading) {
    return <div className={styles.loading}>Loading...</div>;
  }

  if (error) {
    return (
      <div className={styles.page}>
        <ErrorState
          message={error}
          onRetry={refresh}
        />
      </div>
    );
  }

  if (!starter) {
    // This shouldn't render — WelcomeScreen handles it in HomeView
    return null;
  }

  const daysSince = getDaysSinceFeeding(starter.lastFedDate);
  const status = getHealthStatus(daysSince);
  const starterAge = getStarterAgeDays(starter.createdDate);
  const avgInterval = getAverageFeedingInterval(feedings.map((f) => f.date));
  const last10 = feedings.slice(0, 10);

  return (
    <div className={styles.page}>
      {/* Starter Health Card */}
      <HealthIndicator status={status} daysSinceFeeding={daysSince} />

      {/* "I Fed The Starter" Button */}
      <button
        className={styles.feedButton}
        onClick={handleFeedStarter}
        disabled={isFeedingInProgress}
        aria-label="Record a feeding"
      >
        {isFeedingInProgress ? 'Recording...' : '🍞 I Fed The Starter'}
      </button>

      {/* Quick Stats */}
      <Card>
        <div className={styles.statsGrid}>
          <div className={styles.statItem}>
            <div className={styles.statValue}>{starterAge}</div>
            <div className={styles.statLabel}>
              {starterAge === 1 ? 'Day Old' : 'Days Old'}
            </div>
          </div>
          <div className={styles.statItem}>
            <div className={styles.statValue}>{starter.totalFeedings}</div>
            <div className={styles.statLabel}>Total Feedings</div>
          </div>
          <div className={styles.statItem}>
            <div className={styles.statValue}>
              {avgInterval > 0 ? `${avgInterval}` : '—'}
            </div>
            <div className={styles.statLabel}>Avg Days Between</div>
          </div>
        </div>
      </Card>

      {/* Feeding History */}
      {last10.length > 0 && (
        <Card>
          <h2 className={styles.sectionTitle}>Recent Feedings</h2>
          <div className={styles.feedingList} style={{ marginTop: 'var(--spacing-md)' }}>
            {last10.map((feeding, index) => (
              <div key={feeding.id} className={styles.feedingEntry}>
                <div className={styles.feedingInfo}>
                  <div className={styles.feedingDate}>
                    {formatFeedingDate(feeding.date)}
                  </div>
                  <div className={styles.feedingRelative}>
                    {formatRelativeTime(feeding.date)}
                  </div>
                  {feeding.notes && (
                    <div className={styles.feedingNotes}>{feeding.notes}</div>
                  )}
                </div>
                <span className={styles.sourceBadge}>
                  {SOURCE_LABELS[feeding.source] ?? feeding.source}
                </span>
                {index === 0 && (
                  <button
                    className={styles.deleteButton}
                    onClick={() => setShowDeleteConfirm(true)}
                    aria-label="Delete most recent feeding"
                    title="Undo accidental tap"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* "Plan a Bake" CTA */}
      <button
        className={styles.planBakeButton}
        onClick={() => navigate('/schedule')}
        aria-label="Plan a bake"
      >
        📅 Plan a Bake
      </button>

      {/* Toast */}
      {toast && (
        <div className={styles.toast} role="status" aria-live="polite">
          <span>{toast.message}</span>
          {toast.message.includes('fed') && (
            <button className={styles.toastUndo} onClick={handleUndo}>
              Undo
            </button>
          )}
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={showDeleteConfirm}
        title="Delete Feeding?"
        message="Are you sure you want to delete the most recent feeding entry? This cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={handleDeleteRecent}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
}
